/**
 * EventDetail component for displaying full event details
 * Reusable component from @uic/events package
 */

'use client';

import { Calendar, ExternalLink, InfoIcon, Tag, User } from 'lucide-react';
import type { ReactNode } from 'react';
import { AddToCalendar } from '../cards/add-to-calendar';
import {
  EventDateBadgeOverlay,
  EventDateTime,
  EventLocation,
  EventStatusBadge,
  EventTypes,
} from '../components/event';
import { ThemeBadges } from '@uic/taxonomy-themes/components';
import { useEventsComponents } from '../config/context';
import type { Event } from '../types';
import { extractEventTypeData } from '../utils/event-type';

interface EventDetailProps {
  /**
   * Event to display
   */
  event: Event;
  /**
   * Base URL for theme links
   */
  baseUrl?: string;
  /**
   * Optional edit button component (e.g., DrupalEntityEditButton)
   */
  editButton?: ReactNode;
}

/**
 * EventDetail component displays full event information
 * Uses the configuration context for Image and Link components
 */
export function EventDetail({ event, baseUrl = '/events', editButton }: EventDetailProps) {
  const { ImageComponent, LinkComponent } = useEventsComponents();

  // Extract date information
  const startTimestamp = event.eventDateTimeRange?.start?.timestamp;
  const endTimestamp = event.eventDateTimeRange?.end?.timestamp;

  const startDate = startTimestamp ? new Date(startTimestamp * 1000) : null;
  const endDate = endTimestamp ? new Date(endTimestamp * 1000) : null;

  const now = new Date();
  const isPast = endDate ? endDate < now : false;
  const isToday = startDate ? startDate.toDateString() === now.toDateString() : false;

  const isSameDay = Boolean(
    startDate && endDate && startDate.toDateString() === endDate.toDateString()
  );

  // Extract event type data using shared utility (exactly like event-card)
  const { eventTypeColor, firstEventType } = extractEventTypeData(event);

  // Prepare ICS data
  const icsData =
    startDate && endDate
      ? {
          title: event.title,
          startDate,
          endDate,
          location: event.eventLocation || undefined,
          description: event.body?.summary || event.body?.processed || undefined,
          url: event.path || undefined,
        }
      : null;

  // Default illustration component when no image is available (exactly like event-card)
  const EventDefaultIllustration = ({ eventTypeColor }: { eventTypeColor?: string }) => (
    <div
      className="flex h-full w-full items-center justify-center bg-gradient-to-br from-primary/20 to-primary/5"
      style={{
        backgroundColor: eventTypeColor ? `${eventTypeColor}20` : undefined,
      }}
    >
      <Calendar className="h-12 w-12 sm:h-16 sm:w-16 text-background/40" />
    </div>
  );

  return (
    <article className="mx-auto max-w-4xl p-4 space-y-4">
      {/* Event Image - max 1/4 of viewport height */}
      <div className="relative aspect-video max-h-[30vh] w-full overflow-hidden rounded">
        {event.image?.url ? (
          <ImageComponent
            src={event.image.url}
            width={event.image.width ?? 800}
            height={event.image.height ?? 450}
            alt={event.image.alt || event.title}
            className="h-full w-full object-cover transition-transform duration-300"
          />
        ) : (
          <EventDefaultIllustration eventTypeColor={eventTypeColor} />
        )}
        {/* Date badge overlay */}
        {startDate && <EventDateBadgeOverlay date={startDate} />}

        {/* Event type badge with colour - positioned at top right */}
        {firstEventType && (
          <div className="absolute top-2 right-2">
            <EventTypes
              eventTypes={[
                {
                  id: firstEventType.id,
                  name: firstEventType.name,
                  eventTypeColor: eventTypeColor,
                },
              ]}
              variant="card"
            />
          </div>
        )}
        {/* Edit Button */}
        {editButton && (
          <div className="absolute top-2 right-2 sm:top-4 sm:right-4">{editButton}</div>
        )}

        {/* Status badges */}
        <div className="absolute right-2 top-2 sm:right-4 sm:top-4">
          <EventStatusBadge isToday={isToday} isPast={isPast} variant="overlay" />
        </div>
      </div>

      {/* Event Header */}
      <div className="mt-6 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 space-y-4">
            {/* Status Badge */}
            <EventStatusBadge isToday={isToday} isPast={isPast} />

            {/* Title */}
            <h1 className="text-3xl font-bold tracking-tight md:text-4xl">{event.title}</h1>
          </div>

          {/* Action buttons - right side */}
          <div className="flex shrink-0 flex-col items-end gap-2">
            {/* Add to Calendar */}
            {icsData && (
              <AddToCalendar event={icsData} variant="ghost" size="lg" className="rounded-full" />
            )}
          </div>
        </div>
      </div>

      {/* Event Metadata */}

      {/* Date and Time */}
      {startTimestamp && (
        <EventDateTime
          startTimestamp={startTimestamp}
          endTimestamp={endTimestamp}
          startDate={startDate}
          endDate={endDate}
          isSameDay={isSameDay}
        />
      )}

      {/* Location - below title */}
      {event.eventLocation && <EventLocation location={event.eventLocation} variant="default" />}

      {/* External Link */}
      {event.eventExternalLink?.url && (
        <div className="flex items-start gap-3">
          <ExternalLink className="mt-0.5 h-5 w-5 text-muted-foreground" />
          <LinkComponent
            href={event.eventExternalLink.url}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-primary hover:underline"
          >
            {event.eventExternalLink.title || event.eventExternalLink.url}
          </LinkComponent>
        </div>
      )}

      {/* Contact */}
      {(event.eventContactName || event.eventContact?.name) && (
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <User className="h-5 w-5 text-primary" />
          </div>
          <div>
            <div className="font-medium text-xs text-muted-foreground">Contact:</div>
            <div className="font-medium">{event.eventContactName || event.eventContact?.name}</div>
            {event.eventContact?.mail && (
              <LinkComponent
                href={`mailto:${event.eventContact.mail}`}
                className="text-sm text-primary hover:underline"
              >
                {event.eventContact.mail}
              </LinkComponent>
            )}
          </div>
        </div>
      )}

      {/* Themes */}
      {event.theme && event.theme.length > 0 && (
        <div className="flex items-center gap-3">
          <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <Tag className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1">
            <ThemeBadges themes={event.theme} maxVisible={10} showIcon={false} />
          </div>
        </div>
      )}
      {/* Event Description */}
      {event.body?.processed && (
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <InfoIcon className="h-5 w-5 text-primary" />
          </div>
          <div className="prose prose-sm dark:prose-invert max-w-none">
            <div dangerouslySetInnerHTML={{ __html: event.body.processed }} />
          </div>
        </div>
      )}
    </article>
  );
}
